// Building blocks shared by the wave-3 screens (Inbox, integrations, incidents, preview/browser,
// memory): project-role gate that mirrors the API (apps/api/src/auth.ts projectRole), project
// name lookup, short ids, JSON viewers for untrusted data, confirm/reason dialogs and a cursor
// list hook. Every value shown is a server fact.
import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import type { Role } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { useSession } from "../../session/SessionProvider.tsx";
import { listProjects, getProjectDetail } from "../../api/endpoints.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { Dialog } from "../../components/Dialog.tsx";
import { Button } from "../../components/Button.tsx";
import { TextAreaField } from "../../components/Field.tsx";
import { ActionError } from "../../components/ActionError.tsx";
import { Pill } from "../../components/Status.tsx";
import type { IconKind, Tone } from "../../lib/status.ts";

const RANK: Record<Role, number> = { observer: 0, reviewer: 1, operator: 2, maintainer: 3, admin: 4 };

/**
 * Role on a project, computed like the API: an organization admin is admin everywhere; anyone
 * else needs a membership (exact project or "*"). The API enforces it; the UI avoids dead ends.
 */
export function useProjectRole(projectId: string | null): { role: Role | null; atLeast: (min: Role) => boolean } {
  const { me } = useSession();
  let role: Role | null = null;
  if (me && projectId) {
    if (me.role === "admin") role = "admin";
    else {
      const m = (me.project_memberships ?? []).find((x) => x.project_id === projectId) ?? (me.project_memberships ?? []).find((x) => x.project_id === "*");
      role = m ? m.role : null;
    }
  }
  return { role, atLeast: (min) => (role ? RANK[role] >= RANK[min] : false) };
}

export interface ProjectOption {
  id: string;
  name: string;
}

/** Projects the caller can read (first 100), for selectors and name lookup. */
export function useProjectOptions() {
  const state = useAsync((signal) => listProjects({ limit: 100 }, signal), []);
  const options: ProjectOption[] = useMemo(() => (state.data?.items ?? []).map((p) => ({ id: p.id, name: p.name })), [state.data]);
  const nameOf = useCallback((id: string | null | undefined) => (id ? (options.find((p) => p.id === id)?.name ?? null) : null), [options]);
  return { ...state, options, nameOf };
}

export interface ProjectEnvironment {
  id: string;
  kind: string;
  name: string;
  allowed_urls: string[];
}
export interface ProjectWorkspace {
  id: string;
  root_path: string;
  operating_system: string;
}

/** Environments and workspaces of a project (from GET /v1/projects/:id). */
export function useProjectResources(projectId: string) {
  const state = useAsync((signal) => getProjectDetail(projectId, signal), [projectId]);
  const detail = state.data as unknown as { environments?: ProjectEnvironment[]; workspaces?: ProjectWorkspace[] } | undefined;
  return { ...state, environments: detail?.environments ?? [], workspaces: detail?.workspaces ?? [] };
}

export function ShortId({ id, label }: { id: string | null | undefined; label?: string }) {
  if (!id) return <span className="subtle">—</span>;
  return (
    <span className="mono" title={id} aria-label={label ? `${label} ${id}` : id}>
      {id.slice(0, 8)}
    </span>
  );
}

export function Hash({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="subtle">—</span>;
  return (
    <span className="mono hash" title={value}>
      {value.length > 16 ? `${value.slice(0, 12)}…` : value}
    </span>
  );
}

/** Untrusted external data (webhook payload, memory excerpt) shown as inert text. */
export function UntrustedBlock({ value, label }: { value: unknown; label: string }) {
  const text = typeof value === "string" ? value : JSON.stringify(value, null, 2);
  return (
    <pre className="code-block user-content" aria-label={label} tabIndex={0}>
      {text}
    </pre>
  );
}

const TONES: Record<string, { tone: Tone; icon: IconKind }> = {
  success: { tone: "success", icon: "check" },
  danger: { tone: "danger", icon: "x" },
  warning: { tone: "warning", icon: "alert" },
  info: { tone: "info", icon: "circle" },
  neutral: { tone: "neutral", icon: "ban" },
  running: { tone: "info", icon: "play" },
};

/** Pill with a translated label for a known state, or the raw value as a fallback. */
export function StatePill({ tone, label, testId }: { tone: keyof typeof TONES; label: string; testId?: string }) {
  const v = TONES[tone] ?? TONES["info"]!;
  return (
    <span data-testid={testId}>
      <Pill tone={v.tone} icon={v.icon}>
        {label}
      </Pill>
    </span>
  );
}

/** Confirmation dialog for an irreversible or state-changing action. */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  danger = false,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (open) setError(null);
  }, [open]);
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      busy={busy}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button variant={danger ? "danger" : "primary"} loading={busy} onClick={() => void go()}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="stack">
        <div>{body}</div>
        <ActionError error={error} />
      </div>
    </Dialog>
  );
}

/** Dialog that asks for a mandatory reason (reject/tombstone memory sources). */
export function ReasonDialog({
  open,
  title,
  body,
  confirmLabel,
  danger = false,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const { t } = useI18n();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [local, setLocal] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
      setLocal(null);
    }
  }, [open]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      setLocal(t("w3.reasonRequired"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} title={title} busy={busy}>
      <form className="stack" onSubmit={submit} noValidate>
        <div>{body}</div>
        <TextAreaField label={t("w3.reason")} value={reason} onChange={(e) => setReason(e.target.value)} error={local} rows={3} maxLength={2000} required />
        <ActionError error={error} />
        <div className="row row--wrap dialog-actions">
          <Button onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant={danger ? "danger" : "primary"} loading={busy}>
            {confirmLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Cursor-paginated list: first page on dependency change, "load more" appends. */
export function useCursorList<T>(load: (cursor: string | null, signal?: AbortSignal) => Promise<{ items: T[]; next_cursor: string | null }>, deps: readonly unknown[]) {
  const [items, setItems] = useState<T[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [moreError, setMoreError] = useState<unknown>(null);
  const [moreBusy, setMoreBusy] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    setItems(null);
    load(null, controller.signal)
      .then((page) => {
        setItems(page.items);
        setCursor(page.next_cursor);
      })
      .catch((e: unknown) => {
        if (!controller.signal.aborted && !(e instanceof DOMException && e.name === "AbortError")) setError(e);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const loadMore = async () => {
    if (!cursor) return;
    setMoreBusy(true);
    setMoreError(null);
    try {
      const page = await load(cursor);
      setItems((list) => [...(list ?? []), ...page.items]);
      setCursor(page.next_cursor);
    } catch (e) {
      setMoreError(e);
    } finally {
      setMoreBusy(false);
    }
  };
  const reload = useCallback(() => setTick((n) => n + 1), []);
  return { items, cursor, error, moreError, moreBusy, loadMore, reload };
}

/** "Load more" control + end-of-list note shared by cursor lists. */
export function LoadMore({ cursor, busy, count, onMore }: { cursor: string | null; busy: boolean; count: number; onMore: () => void }) {
  const { t } = useI18n();
  if (cursor)
    return (
      <div>
        <Button size="sm" onClick={onMore} loading={busy}>
          {t("obs.loadMore")}
        </Button>
      </div>
    );
  if (count > 0) return <p className="field-hint">{t("w3.endOfList", { n: count })}</p>;
  return null;
}

/** Splits a textarea/comma list into trimmed non-empty entries. */
export function splitList(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}
